import { mapAnswerFromQuestion, isEmpty } from "@/common/utils";
import { VuexModule, Module, Mutation, Action } from "vuex-module-decorators";

import {
  Question,
  SurveyType,
  SurveyActionType,
  Term,
  SurveySection,
  Answer,
  QuestionType,
  TPTPlaceResult,
} from "@/interfaces";

import {
  getSurvey,
  getTermsBySection,
  newSurvey,
  updateSurvey,
  updateNewSurvey,
  updateNewSurveyPut,
  getSurveyByID,
} from "@/services";

import { groupBy, uniqBy } from "lodash";

import store from "@/store";

export interface SurveyState {
  type: SurveyType | null;
  actionType: SurveyActionType | null;
  surveyId: string | null;
  termsBySection: Term[];
  scrollToTerm: string;
  allQuestions: Question[];
  questions: Question[];
  dictionaries: { [Key: string]: { [Key: string]: { [Key: string]: string } } };
  answers: Answer[];
  sections: SurveySection[];
  questionsGrouped: { [key: number]: Question[] };
  currentPage: number;
  currentSection: number;
  errorMessage: string | null;
  loading: boolean;
  loadingPreSurvey: boolean;
  completing: boolean;
  canExit: boolean;
}

export const initialState: SurveyState = {
  type: null,
  actionType: null,
  surveyId: null,
  termsBySection: [],
  scrollToTerm: "",
  allQuestions: [],
  questions: [],
  dictionaries: {},
  answers: [],
  sections: [],
  questionsGrouped: {},
  currentPage: 1,
  currentSection: 0,
  errorMessage: null,
  loading: false,
  loadingPreSurvey: false,
  completing: false,
  canExit: false,
};

@Module({ name: "survey", namespaced: true, store })
export default class Survey extends VuexModule implements SurveyState {
  type: SurveyType | null = null;
  actionType: SurveyActionType | null = null;
  surveyId: string | null = null;
  termsBySection: Term[] = [];
  scrollToTerm = "";
  allQuestions: Question[] = [];
  questions: Question[] = [];
  dictionaries: { [Key: string]: { [Key: string]: { [Key: string]: string } } } = {};
  answers: Answer[] = [];
  sections: SurveySection[] = [];
  questionsGrouped: { [key: number]: Question[] } = {};
  currentPage = 1;
  currentSection = 0;
  errorMessage: string | null = null;
  loading = false;
  loadingPreSurvey = false;
  completing = false;
  canExit = false;

  @Action({ rawError: true })
  async getSurvey(type: SurveyType) {
    try {
      this.context.commit("setLoading", true);
      const result = await getSurvey(type);
      this.context.commit("setSections", result.sections);
      let resultQuestions: Question[] = [];
      result.sections.forEach((section, sectionIndex) => {
        const questions: Question[] = section.questions
          .filter(q => !(q.conf && q.conf.show_on))
          .map((question, index) => ({
            ...question,
            index: index + 1,
            section: sectionIndex,
          }));

        section.questions
          .filter(q => q.conf && q.conf.show_on)
          .forEach(question => {
            if (question.conf && question.conf.show_on) {
              const confShowOn = question.conf.show_on;
              const bindKey = "id";
              const parentIndex = questions.findIndex(el => el.id === confShowOn[bindKey]);

              if (parentIndex !== -1) {
                const questionShowIf = section.questions.filter(
                  currQuestion =>
                    currQuestion.conf &&
                    currQuestion.conf.show_on &&
                    currQuestion.conf.show_on[bindKey] === confShowOn[bindKey],
                );

                const subIndex = questionShowIf.findIndex(qShowIf => qShowIf.id === question.id);

                if (subIndex !== -1) {
                  const newQuestion: Question = {
                    ...question,
                    index: questions[parentIndex].index,
                    subIndex: subIndex + 1,
                  };

                  questions.splice(parentIndex + subIndex + 1, 0, newQuestion);
                }
              }
            }
          });

        resultQuestions = [...resultQuestions, ...questions];
      });

      if (result.dictionaries) {
        this.context.commit("setDictionaries", result.dictionaries);
      }

      this.context.commit("setAllQuestions", resultQuestions);
      const questionsGrouped = groupBy(resultQuestions, "page");
      const questions = questionsGrouped[this.currentPage];

      const answers = mapAnswerFromQuestion(resultQuestions);

      this.context.commit("setAnswers", answers);
      this.context.commit("surveySuccess", { questions, questionsGrouped, type });
      this.context.commit("setLoading", false);
    } catch (error) {
      this.context.commit("surveyFail", error);
      this.context.commit("setLoading", false);
      return Promise.reject(error);
    }
  }

  @Action({ rawError: true })
  async getSurveyByID(request: { type: SurveyType; id: string; actionType: string }) {
    try {
      this.context.commit("setLoading", true);
      const result = await getSurveyByID(request.type, request.id, request.actionType);
      if (request.actionType) {
        this.context.commit("setActionType", request.actionType as SurveyActionType);
      }
      this.context.commit("setSections", result.sections);
      let resultQuestions: Question[] = [];
      result.sections.forEach((section, sectionIndex) => {
        const questions: Question[] = section.questions
          .filter(q => !(q.conf && q.conf.show_on))
          .map((question, index) => ({
            ...question,
            index: index + 1,
            section: sectionIndex,
          }));

        section.questions
          .filter(q => q.conf && q.conf.show_on)
          .forEach(question => {
            if (question.conf && question.conf.show_on) {
              const confShowOn = question.conf.show_on;
              const bindKey = "id";
              const parentIndex = questions.findIndex(el => el.id === confShowOn[bindKey]);

              if (parentIndex !== -1) {
                const questionShowIf = section.questions.filter(
                  currQuestion =>
                    currQuestion.conf &&
                    currQuestion.conf.show_on &&
                    currQuestion.conf.show_on[bindKey] === confShowOn[bindKey],
                );

                const subIndex = questionShowIf.findIndex(qShowIf => qShowIf.id === question.id);

                if (subIndex !== -1) {
                  const newQuestion: Question = {
                    ...question,
                    index: questions[parentIndex].index,
                    subIndex: subIndex + 1,
                  };

                  questions.splice(parentIndex + subIndex + 1, 0, newQuestion);
                }
              }
            }
          });

        resultQuestions = [...resultQuestions, ...questions];
      });

      if (result.dictionaries) {
        this.context.commit("setDictionaries", result.dictionaries);
      }

      this.context.commit("setAllQuestions", resultQuestions);
      const questionsGrouped = groupBy(resultQuestions, "page");
      const questions = questionsGrouped[this.currentPage];

      const mappedAnswer = mapAnswerFromQuestion(resultQuestions);
      const answers = mappedAnswer.map(answer => {
        if (result.answers) {
          const find = result.answers.find(ans => ans.id === answer.id);

          if (find) {
            const findQuestion = resultQuestions.find(question => question.id === answer.id);

            if (findQuestion) {
              if (findQuestion.type === QuestionType.CHECKBOX_WITH_MAP) {
                const options = findQuestion.options as any;
                const values = options.filter((opt: TPTPlaceResult) => find.answer.includes(opt.id));

                return {
                  ...answer,
                  answer: values,
                };
              }

              return find;
            }
          }
        }

        return answer;
      });

      this.context.commit("setAnswers", answers);
      this.context.commit("surveySuccess", { questions, questionsGrouped, type: request.type });
      this.context.commit("setSurveyId", request.id);
      this.context.commit("setLoading", false);
    } catch (error) {
      this.context.commit("surveyFail", error);
      this.context.commit("setLoading", false);
      return Promise.reject(error);
    }
  }

  @Action({ rawError: true })
  async createSurvey() {
    try {
      if (this.type !== null) {
        this.context.commit("setCompleting", true);
        await newSurvey(this.type, this.answersToSend);
      }
    } catch (error) {
      this.context.commit("surveyFail", error);
      return Promise.reject(error);
    } finally {
      this.context.commit("setCompleting", false);
    }
  }

  @Action({ rawError: true })
  async updateSurvey(id: string) {
    try {
      if (this.type !== null) {
        this.context.commit("setCompleting", true);
        if (this.actionType) {
          if (this.actionType === SurveyActionType.UPDATE) {
            await updateNewSurveyPut(this.type, id, this.answersToSend, this.actionType);
          } else {
            await updateNewSurvey(this.type, id, this.answersToSend, this.actionType);
          }
        } else {
          await updateSurvey(this.type, id, this.answersToSend);
        }
      }
    } catch (error) {
      this.context.commit("surveyFail", error);
      return Promise.reject(error);
    } finally {
      this.context.commit("setCompleting", false);
    }
  }

  @Action({ rawError: true })
  async getPreSurvey(type: SurveyType) {
    try {
      const terms = await getTermsBySection(type);
      this.context.commit("setTermsBySection", terms);
      this.context.commit("setLoadingPreSurvey", true);
    } catch (error) {
      return Promise.reject(error);
    } finally {
      this.context.commit("setLoadingPreSurvey", false);
    }
  }

  @Action({ rawError: true })
  setNewAnswers(answers: Answer[]) {
    this.context.commit("setAnswers", answers);
  }

  @Mutation
  setTermsBySection(terms: Term[]) {
    this.termsBySection = terms;
  }

  @Mutation
  setSurveyType(type: SurveyType | null) {
    this.type = type;
  }

  @Mutation
  setActionType(actionType: SurveyActionType | null) {
    this.actionType = actionType;
  }

  @Mutation
  setAllQuestions(questions: Question[]) {
    this.allQuestions = questions;
  }

  @Mutation
  setSurveyId(id: string | null) {
    this.surveyId = id;
  }

  @Mutation
  setSections(sections: SurveySection[]) {
    this.sections = sections;
  }

  @Mutation
  setDictionaries(dictionaries: { [Key: string]: { [Key: string]: { [Key: string]: string } } }) {
    this.dictionaries = dictionaries;
  }

  @Mutation
  setAnswers(answers: Answer[]) {
    this.answers = uniqBy([...answers, ...this.answers], "id");
  }

  @Mutation
  setCanExit(canExit: boolean) {
    this.canExit = canExit;
  }

  @Mutation
  reset() {
    this.questions = [];
    this.answers = [];
    this.questionsGrouped = {};
    this.currentPage = 1;
    this.currentSection = 0;
    this.errorMessage = null;
    this.loading = false;
    this.loadingPreSurvey = false;
    this.canExit = false;
    this.type = null;
    this.actionType = null;
    this.surveyId = null;
  }

  @Mutation
  surveySuccess(result: { questions: Question[]; questionsGrouped: { [key: number]: Question[] }; type: SurveyType }) {
    this.questions = result.questions;
    this.questionsGrouped = result.questionsGrouped;
    this.type = result.type;
  }

  @Mutation
  surveyFail(error: string) {
    this.errorMessage = error;
  }

  @Mutation
  setLoading(status: boolean) {
    this.loading = status;
  }

  @Mutation
  setCompleting(status: boolean) {
    this.completing = status;
  }

  @Mutation
  setLoadingPreSurvey(status: boolean) {
    this.loadingPreSurvey = status;
  }

  @Mutation
  setNextPage() {
    const nextPage = this.currentPage + 1;
    if (nextPage <= Object.keys(this.questionsGrouped).length) {
      this.currentPage = nextPage;

      this.questions = this.questionsGrouped[this.currentPage];

      if (this.questions.length > 0 && this.questions[0].section !== undefined) {
        this.currentSection = this.questions[0].section;
      }
    }
  }

  @Mutation
  setPrevPage() {
    const prevPage = this.currentPage - 1;
    if (prevPage > 0) {
      this.currentPage = prevPage;

      this.questions = this.questionsGrouped[this.currentPage];

      if (this.questions.length > 0 && this.questions[0].section !== undefined) {
        this.currentSection = this.questions[0].section;
      }
    }
  }

  @Mutation
  setScrollToTerm(scrollToTerm: string) {
    this.scrollToTerm = scrollToTerm;
  }

  get canGoNext() {
    return this.currentPage < Object.keys(this.questionsGrouped).length;
  }

  get canGoPrev() {
    return this.currentPage > 1;
  }

  get visibleSection() {
    if (this.sections.length > 0) {
      return this.sections[this.currentSection];
    }

    return null;
  }

  get sectionsCount() {
    return this.sections.length;
  }

  get answersToSend() {
    const answers = [...this.answers];
    const showIfId = "id";
    const valueKey = "value";

    this.allQuestions.forEach(question => {
      const questionShowIf = this.allQuestions.filter(
        currQuestion =>
          currQuestion.conf && currQuestion.conf.show_on && currQuestion.conf.show_on[showIfId] === question.id,
      );

      if (questionShowIf.length > 0) {
        questionShowIf.forEach(question => {
          const questionIndex = this.answers.findIndex(answer => answer.id === question.id);
          const value = question.conf && question.conf.show_on ? question.conf.show_on[valueKey] : "";
          let parentIfIndex = -1;
          if (question.conf && question.conf.show_on) {
            const parentAnswerId = question.conf.show_on[showIfId];
            parentIfIndex = this.answers.findIndex(answer => answer.id === parentAnswerId);
          }

          if (parentIfIndex !== -1) {
            const parentValue = answers[parentIfIndex].answer;

            if (questionIndex !== -1 && parentValue !== value) {
              const mappedQuestion = mapAnswerFromQuestion([question]);
              answers[questionIndex] = {
                ...answers[questionIndex],
                answer: mappedQuestion[0].answer,
              };
            }
          }
        });
      }

      const questionShowIfChild = this.allQuestions.filter(currQuestion => {
        if (currQuestion.type === QuestionType.GROUP && currQuestion.items) {
          const filteredChild = currQuestion.items.filter(item => item.conf && item.conf.show_on);
          return filteredChild.length > 0;
        } else if (currQuestion.type === QuestionType.REPEATER && currQuestion.elements) {
          const filteredChild = currQuestion.elements.items.filter(item => item.conf && item.conf.show_on);
          return filteredChild.length > 0;
        }

        return false;
      });

      if (questionShowIfChild.length > 0) {
        questionShowIfChild.forEach(question => {
          const questionIndex = this.answers.findIndex(answer => answer.id === question.id);

          if (question.type === QuestionType.GROUP && question.items) {
            question.items.forEach(item => {
              const value = item.conf && item.conf.show_on ? item.conf.show_on[valueKey] : "";

              if (item.conf && item.conf.show_on) {
                const parentAnswerId = item.conf.show_on[showIfId];
                const answerGroup = Object.assign({}, this.answers[questionIndex]);

                if (answerGroup.answer && answerGroup.answer[parentAnswerId]) {
                  if (answerGroup.answer[parentAnswerId] !== value) {
                    const answerValue = Object.assign({}, answerGroup.answer);
                    delete answerValue[item.id];
                    const mappedAnswers = mapAnswerFromQuestion([item]);

                    answerValue[item.id] = mappedAnswers[0].answer;

                    answerGroup.answer = answerValue;
                    answers[questionIndex] = {
                      ...answerGroup,
                    };
                  }
                }
              }
            });
          }
        });
      }
    });

    return answers;
  }

  get valueFromDictionaries() {
    return (dictName: string, searchedValue: string, valueToGet: string) => {
      if (!isEmpty(this.dictionaries)) {
        return this.dictionaries[dictName][searchedValue][valueToGet];
      }

      return "Non trovato";
    };
  }
}
